/**
 * Collapsed multi-select dropdowns normally summarise their state as
 * "All Properties" or "3 selected". When the selection resolves to exactly one
 * option that summary hides the only thing worth showing, so the trigger reads
 * the option itself instead — a Properties filter whose sole option is "Fresh"
 * shows "Fresh", not "All Properties".
 *
 * Returns null when the selection is not a single option, so callers keep their
 * own all/n-selected/empty labels for every other case. A chip may name a value
 * only when every plotted row carries it, so when the lone selection is also the
 * whole option list and rows without a value are still plotted (unknownRetained)
 * it returns null too and the caller's "All ..." label applies.
 *
 * @param {Array} selected - Currently selected values
 * @param {Array} options - Valid option values (empty/absent = no filtering)
 * @param {Function} [getOptionLabel] - (value) => display string for the value
 * @param {Object} [opts]
 * @param {boolean} [opts.unknownRetained] - Plotted rows still include rows with no value
 * @returns {string|null}
 */
export function singleSelectionLabel(selected, options, getOptionLabel, { unknownRetained = false } = {}) {
  const sel = Array.isArray(selected) ? selected : [];
  const opts = Array.isArray(options) ? options : [];
  const active = opts.length > 0
    ? sel.filter((v) => opts.some((o) => String(o) === String(v)))
    : sel;
  if (active.length !== 1) return null;
  if (unknownRetained && active.length === opts.length) return null;
  const only = active[0];
  const label = typeof getOptionLabel === 'function' ? getOptionLabel(only) : only;
  return label == null || String(label).trim() === '' ? null : String(label);
}

export function toggleMultiValue(item, selected, max) {
  const sel = Array.isArray(selected) ? selected : [];
  if (sel.includes(item)) return sel.filter((i) => i !== item);
  if (max && sel.length >= max) return [...sel.slice(sel.length - max + 1), item];
  return [...sel, item];
}

/** SKU/package pin is live only in package price-unit mode. */
export function packagePinActive(priceUnit) {
  return priceUnit === 'package';
}

/** True when the user narrowed the dim, so chart series should name that value. */
export function shouldNameSeriesForMulti(selected, options) {
  const opts = Array.isArray(options) ? options : [];
  const sel = Array.isArray(selected) ? selected : [];
  if (opts.length === 0 || sel.length === 0) return false;
  return sel.length < opts.length;
}

export function applyMultiOrAllFilter(rows, field, selected, allOptions, getValue) {
  const opts = allOptions || [];
  const sel = Array.isArray(selected) ? selected : [];
  if (!opts.length) return rows;
  if (!sel.length || sel.length >= opts.length) return rows;
  const pick = getValue || ((r) => r[field]);
  return rows.filter((r) => {
    const v = pick(r);
    return v && sel.includes(v);
  });
}
